import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram, Send, Youtube, Twitter, Linkedin, Music2, Link as LinkIcon } from 'lucide-react';

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, telegram: Send, youtube: Youtube, x: Twitter, twitter: Twitter, linkedin: Linkedin, tiktok: Music2 };
import Logo from '@/components/Logo';
import { useLanguage } from '@/context/LanguageContext';

export default function Footer() {
  const { t } = useLanguage();
  const platform = [
    { to: '/services', key: 'nav.services' },
    { to: '/apps', key: 'apps.eyebrow' },
    { to: '/download', key: 'nav.download' },
    { to: '/faq', key: 'faqPage.eyebrow' },
    { to: '/maintenance', key: 'nav.maintenance' },
  ];
  const company = [
    { to: '/about', key: 'nav.about' },
    { to: '/solutions', key: 'nav.solutions' },
    { to: '/offers', key: 'nav.offers' },
    { to: '/contact', key: 'nav.contact' },
    { to: '/admin', key: 'admin.title' },
  ];

  return (
    <footer className="border-t border-brand-line bg-white" data-testid="footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 md:grid-cols-12 lg:px-8">
        <div className="md:col-span-5">
          <Logo />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-brand-steel">{t('footer.tag')}</p>
          <p className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-brand-dim">
            <MapPin className="h-3.5 w-3.5 text-brand-blue" /> {t('footer.address')}
          </p>
          <div className="mt-6 flex items-center gap-2" data-testid="footer-socials">
            {t('settings.socials').map(({ id, url }) => {
              const Icon = SOCIAL_ICONS[id] || LinkIcon;
              return (
              <a key={id} href={url || '#'} target="_blank" rel="noreferrer" aria-label={id} data-testid={`footer-social-${id}`} className="grid h-9 w-9 place-items-center rounded-full border border-brand-line text-brand-steel transition-all duration-300 hover:border-brand-blue hover:text-brand-blue">
                <Icon className="h-4 w-4" />
              </a>
              );
            })}
          </div>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-widest text-brand-dim">{t('footer.platform')}</p>
          <ul className="mt-4 space-y-2.5">
            {platform.map((l) => (
              <li key={l.to}><Link to={l.to} data-testid={`footer-link-${l.to.slice(1)}`} className="text-sm font-semibold text-brand-steel transition-colors hover:text-brand-blue">{t(l.key)}</Link></li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-widest text-brand-dim">{t('footer.company')}</p>
          <ul className="mt-4 space-y-2.5">
            {company.map((l) => (
              <li key={l.to}><Link to={l.to} data-testid={`footer-link-${l.to.slice(1)}`} className="text-sm font-semibold text-brand-steel transition-colors hover:text-brand-blue">{t(l.key)}</Link></li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-widest text-brand-dim">{t('footer.contactCol')}</p>
          <ul className="mt-4 space-y-2.5 text-sm font-semibold text-brand-steel">
            <li className="inline-flex items-center gap-2"><Phone className="h-4 w-4 text-brand-blue" /><a href={`tel:${t('settings.phone').replace(/\s/g, '')}`} dir="ltr" data-testid="footer-phone">{t('settings.phone')}</a></li>
            <li className="inline-flex items-center gap-2"><Mail className="h-4 w-4 text-brand-blue" /><a href={`mailto:${t('settings.email')}`} data-testid="footer-email">{t('settings.email')}</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-brand-line/70">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 lg:px-8">
          <p className="text-xs text-brand-dim">{t('footer.rights')}</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-brand-dim">{t('settings.coords')}</p>
        </div>
      </div>
    </footer>
  );
}
